import ABRCodeSelect from 'Components/Generic/BOSFilterSelect/ABRCodeSelect';
import AccountSelect from 'Components/Generic/BOSFilterSelect/AccountSelect';
import FiscalYearSelect from 'Components/Generic/BOSFilterSelect/FiscalYearSelect';
import OrganizationSelect from 'Components/Generic/BOSFilterSelect/OrganizationSelect';
import ButtonFooter from 'Components/Generic/Buttons/ButtonFooter';
import CancelButton from 'Components/Generic/Buttons/CancelButton';
import DeleteButton from 'Components/Generic/Buttons/DeleteButton';
import SaveButton from 'Components/Generic/Buttons/SaveButton';
import UndoButton from 'Components/Generic/Buttons/UndoButton';
import DetailTable from 'Components/Generic/DetailPanel/DetailTable';
import EditableDetailPanel from 'Components/Generic/DetailPanel/EditableDetailPanel';
import BudgetData from 'Data/BudgetData';
import { useAlertContext } from 'Data/context/Alerts';
import { getOrgNameFromId } from 'Data/context/Organizations';
import { user_roles } from 'Data/Enum';
import SiteDrawerData from 'Data/SiteDrawerData';
import DataManager from 'Lib/DataManager';
import { useUndoableSaveAction } from 'Lib/hooks/useUndoableSaveAction';
import { useUpdatableObject } from 'Lib/hooks/useUpdatableObject';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { formatMoney, isUserAuthorized } from 'Util';

let DM = new DataManager();

const BudgetDetails = ({ budgetId, budgetObject }) => {
  const { openDeleteDialog, showDeletedToast } = useAlertContext();

  const {
    currentObject: budget,
    hasBeenUpdated,
    reset: resetBudget,
    updateField,
    originalObject: originalBudget,
    doSave,
  } = useUpdatableObject(BudgetData.updateRecord, BudgetData.createRecord);
  const [resetUndo, showSaveUndoToast] = useUndoableSaveAction({
    originalObject: originalBudget,
    undoFunction: BudgetData.updateRecord,
    onUndo: (originalEC) => fetchBudget(originalEC.id),
    objectType: 'Budget',
  });

  let editable = isUserAuthorized({
    roles: [
      user_roles.ADMINISTRATOR,
      // [budget?.parent_organization_id || '*', user_org_roles.MANAGER.key],
    ],
  });

  useEffect(() => {
    DM.add(
      BudgetData.registerListener('BLANK_RECORD_LOADED', onBudgetLoaded),
      BudgetData,
    );
    DM.add(
      BudgetData.registerListener('RECORD_LOADED', onBudgetLoaded),
      BudgetData,
    );
    DM.add(
      BudgetData.registerListener('RECORD_UPDATED', onBudgetUpdated),
      BudgetData,
    );
    DM.add(
      BudgetData.registerListener('RECORDS_DELETED', onBudgetDeleted),
      BudgetData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  useEffect(() => {
    if (!budgetId) {
      BudgetData.getBlank();
    }
  }, []);

  useEffect(() => {
    if (budgetId > 0) {
      fetchBudget(budgetId);
    }
  }, [budgetId]);

  useEffect(() => {
    if (!editable) {
      resetBudget(budgetObject);
    } else if (budgetObject?.id && Number(budgetObject.id) >= 0) {
      fetchBudget(budgetObject.id);
    }
  }, [budgetObject]);

  const fetchBudget = (id) => {
    BudgetData.getRecord(id, { _include: 'account' });
  };

  const onBudgetLoaded = (payload) => {
    if (payload) {
      resetBudget(payload);
      resetUndo(() => fetchBudget(payload.id));
    }
  };

  const onBudgetUpdated = (payload) => {
    if (payload) {
      showSaveUndoToast();
    }
  };

  let delCancelButton =
    editable &&
    (budgetId ? (
      <DeleteButton
        onDeleteItem={() => {
          deleteClicked();
        }}
        tooltipText={'Delete Budget'}
      />
    ) : (
      <CancelButton onCancel={SiteDrawerData.close} />
    ));

  const deleteBudget = () => {
    BudgetData.deleteRecords([budgetId]);
  };

  const deleteClicked = () => {
    openDeleteDialog({
      objectType: 'Budget',
      onConfirmDelete: deleteBudget,
    });
  };

  const onBudgetDeleted = () => {
    showDeletedToast('Budget');
  };

  let detailData = [
    {
      label: 'Fiscal Year',
      value: {
        render: (inEditState) => {
          return inEditState ? (
            <FiscalYearSelect
              defaultSelectedYear={`${budget.fiscal_year}`}
              onSelectFiscalYear={(year) =>
                updateField('fiscal_year', year?.year || '')
              }
              fill
              autoMarginLeft
            />
          ) : (
            budget.fiscal_year
          );
        },
      },
      isRequired: true,
    },
    {
      label: 'Core Facility',
      value: {
        render: (inEditState) => {
          return inEditState ? (
            <OrganizationSelect
              key={budget.parent_organization_id}
              defaultSelectedId={budget.parent_organization_id}
              onSelectOrganization={(org) =>
                updateField('parent_organization_id', org?.id || null)
              }
            />
          ) : (
            getOrgNameFromId(budget.parent_organization_id)
          );
        },
      },
    },
    {
      label: 'Expense Code',
      value: {
        render: (inEditState) => {
          return inEditState ? (
            <ABRCodeSelect
              defaultSelectedABR={budget.abr_code_fk}
              onSelectABRCode={(abr) => {
                abr && updateField('abr_code_fk', abr.abr_code);
              }}
            />
          ) : (
            budget.abr_code_fk
          );
        },
      },
    },
    {
      label: 'Account',
      value: {
        render: (inEditState) => {
          return inEditState ? (
            <AccountSelect
              orgId={budget.parent_organization_id}
              defaultSelectedId={budget.account_id}
              onSelectAccount={(act) => {
                act && updateField('account_id', act.id);
              }}
            />
          ) : (
            `${budget?.charge_account?.code || ''}  ${
              budget?.charge_account?.name || ''
            }`
          );
        },
      },
    },
    {
      label: 'Description',
      value: budget.description,
      editableFields: ['value'],
      onEdit: (newValue) => {
        updateField('description', newValue);
      },
    },
    {
      label: 'Amount',
      value: budget.amount,
      format: (value, fieldName) =>
        fieldName === 'value' ? `$${formatMoney(value)}` : value,
      editableFields: ['value'],
      onEdit: (newValue) => {
        updateField('amount', newValue);
      },
    },
  ];

  const undoButton = editable && budget.id && hasBeenUpdated && (
    <UndoButton clickHandler={() => fetchBudget(budget.id)} />
  );

  const saveButton = editable && hasBeenUpdated && (
    <SaveButton clickHandler={doSave} />
  );

  const footer = (
    <ButtonFooter>
      {undoButton}
      {saveButton}
    </ButtonFooter>
  );

  return (
    budget && (
      <div
        style={{
          display: 'flex',
          gap: 10,
          flexDirection: 'column',
          maxWidth: 600,
        }}>
        <EditableDetailPanel
          editable={editable}
          renderChild={(inEditState) => {
            return (
              <DetailTable
                data={detailData}
                editable={!budget?.id || inEditState}
              />
            );
          }}
          renderFooter={() => footer}
          hideToggle={!budget?.id}
        />
        {delCancelButton}
      </div>
    )
  );
};

BudgetDetails.propTypes = {
  budgetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  budgetObject: PropTypes.object,
};

export default BudgetDetails;
